<script setup lang="ts">
import {onMounted, ref} from "vue";
import {useRoute} from "vue-router";
import youtubeVideoService from "@/services/YoutubeVideoService";
import type {YoutubeVideo} from "@/models/youtube_video";
import mdmService from "@/services/MdmService";
import {RouteNames} from "@/router/routeNames";
import YouTubePlayer from "@/components/YouTubePlayer.vue"

const route = useRoute();

let videoID = '';
let showPurplePlayer = ref(true);

const video = ref<YoutubeVideo>({
  video_id: '',
  description: '',
  channel_id: '',
  published_at: '',
  thumbnail: '',
  title: '',
});

const loading = ref<boolean>(true);

onMounted(() => {
  videoID = route.params.videoId as string;
  showPurplePlayer.value = route.query.showPurplePlayer as string === 'true';
  getVideo();
})

async function getVideo() {
  try {
    video.value = await youtubeVideoService.get(videoID);
    await mdmService.sendActivityLog('ViewedVideo', video.value.title);
    loading.value = false;
  } catch (e) {
    console.error(e);
  }
}
</script>

<template>
  <div class="video-container" v-if="!loading">
    <RouterLink :to="{name: RouteNames.Channel, params: {channelId: video.channel_id}}" class="back-link">
      ← Back to channel
    </RouterLink>

    <YouTubePlayer v-if="showPurplePlayer" :video-id="videoID"/>

    <div v-else class="video-wrapper">
      <iframe
          :src="`https://www.youtube.com/embed/${videoID}?modestbranding=1&rel=0&iv_load_policy=3`"
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
      ></iframe>
    </div>

    <div class="video-info">
      <h2 class="video-title">{{ video.title }}</h2>
      <p class="video-description">{{ video.description }}</p>
    </div>
  </div>
</template>

<style scoped>
.video-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.back-link {
  align-self: flex-start;
  padding: 0 16px 16px;
  font-weight: bold;
  color: var(--color-text);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

.video-wrapper {
  position: relative;
  padding-bottom: 56.25%;
  width: 100%;
}

iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.video-info {
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.video-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: bold;
}

.video-description {
  margin: 0;
  font-size: 14px;
  white-space: pre-line;
}
</style>

